'use client';

import { Box } from '@chakra-ui/react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

const WIDTH_STORAGE_KEY = 'attune:workspace-doc-panel-width';
const EDITOR_WIDTH_STORAGE_KEY = 'attune:workspace-editor-panel-width';
export const SIDE_PANEL_DEFAULT_WIDTH = 620;
export const SIDE_PANEL_MIN_WIDTH = 460;
export const SIDE_PANEL_MAX_WIDTH = 980;
export const SIDE_PANEL_MAIN_MIN_WIDTH = 420;

export function clampSidePanelWidth(width: number, availableWidth = SIDE_PANEL_MAX_WIDTH) {
  const maximum = Math.max(0, Math.min(SIDE_PANEL_MAX_WIDTH, availableWidth));
  return Math.min(maximum, Math.max(Math.min(SIDE_PANEL_MIN_WIDTH, maximum), width));
}

const SidePanelSizeContext = createContext<{
  panelWidth: number;
  setPanelWidth: (width: number) => void;
  panelWidthValue: string;
} | null>(null);

export function SidePanelSizeProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const params = useSearchParams();
  const editorWorkspace = pathname === '/app/workspace' &&
    (params.get('editor') === 'canvas' || params.get('editor') === 'website');
  const [documentWidth, setDocumentWidth] = useState(SIDE_PANEL_DEFAULT_WIDTH);
  const [editorWidth, setEditorWidth] = useState(SIDE_PANEL_MAX_WIDTH);
  const panelWidth = editorWorkspace ? editorWidth : documentWidth;
  const setPanelWidth = editorWorkspace ? setEditorWidth : setDocumentWidth;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem(WIDTH_STORAGE_KEY);
      if (stored !== null && Number.isFinite(Number(stored))) {
        setDocumentWidth(clampSidePanelWidth(Number(stored)));
      }
      const editorStored = window.localStorage.getItem(EDITOR_WIDTH_STORAGE_KEY);
      if (editorStored !== null && Number.isFinite(Number(editorStored))) {
        setEditorWidth(clampSidePanelWidth(Number(editorStored)));
      }
    } catch {
      // Resizing remains available when browser storage is disabled.
    }
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    try {
      window.localStorage.setItem(WIDTH_STORAGE_KEY, String(documentWidth));
      window.localStorage.setItem(EDITOR_WIDTH_STORAGE_KEY, String(editorWidth));
    } catch {
      // Keep the chosen width for this workspace session.
    }
  }, [documentWidth, editorWidth, ready]);

  const value = useMemo(
    () => ({
      panelWidth,
      setPanelWidth,
      panelWidthValue: `min(max(${SIDE_PANEL_MIN_WIDTH}px, ${panelWidth}px), max(0px, calc(100% - ${SIDE_PANEL_MAIN_MIN_WIDTH}px)))`,
    }),
    [panelWidth, setPanelWidth]
  );

  return <SidePanelSizeContext.Provider value={value}>{children}</SidePanelSizeContext.Provider>;
}

export function useSidePanelSize() {
  const context = useContext(SidePanelSizeContext);
  if (!context) throw new Error('useSidePanelSize must be used within SidePanelSizeProvider');
  return context;
}

export function SidePanelResizeHandle({
  label,
  disabled = false,
}: {
  label: string;
  disabled?: boolean;
}) {
  const { setPanelWidth } = useSidePanelSize();
  const handleRef = useRef<HTMLDivElement>(null);
  const [measuredSize, setMeasuredSize] = useState<{
    min: number;
    max: number;
    now: number;
  } | null>(null);
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef<{
    pointerId: number;
    startX: number;
    startWidth: number;
    availableWidth: number;
  } | null>(null);

  useLayoutEffect(() => {
    const panel = handleRef.current?.parentElement;
    const container = panel?.parentElement;
    if (disabled || !panel || !container) return;

    const measure = () => {
      const max = Math.max(
        0,
        Math.round(
          Math.min(
            SIDE_PANEL_MAX_WIDTH,
            container.getBoundingClientRect().width - SIDE_PANEL_MAIN_MIN_WIDTH
          )
        )
      );
      const min = Math.min(SIDE_PANEL_MIN_WIDTH, max);
      const now = Math.min(max, Math.max(min, Math.round(panel.getBoundingClientRect().width)));
      setMeasuredSize(previous =>
        previous?.min === min && previous.max === max && previous.now === now
          ? previous
          : { min, max, now }
      );
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(panel);
    observer?.observe(container);
    return () => observer?.disconnect();
  }, [disabled]);

  return (
    <Box
      ref={handleRef}
      role="separator"
      aria-label={label}
      aria-orientation="vertical"
      aria-valuemin={measuredSize?.min}
      aria-valuemax={measuredSize?.max}
      aria-valuenow={measuredSize?.now}
      tabIndex={0}
      display={disabled ? 'none' : { base: 'none', lg: 'block' }}
      position="absolute"
      left="-4px"
      top="0"
      bottom="0"
      w="8px"
      cursor="col-resize"
      touchAction="none"
      userSelect="none"
      zIndex="docked"
      onPointerDown={event => {
        if (event.button !== 0) return;
        const panel = event.currentTarget.parentElement;
        const container = panel?.parentElement;
        if (!panel || !container) return;
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        dragRef.current = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startWidth: panel.getBoundingClientRect().width,
          availableWidth: container.getBoundingClientRect().width - SIDE_PANEL_MAIN_MIN_WIDTH,
        };
        setResizing(true);
      }}
      onPointerMove={event => {
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        setPanelWidth(
          clampSidePanelWidth(drag.startWidth + drag.startX - event.clientX, drag.availableWidth)
        );
      }}
      onPointerUp={event => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
          event.currentTarget.releasePointerCapture(event.pointerId);
        }
        dragRef.current = null;
        setResizing(false);
      }}
      onPointerCancel={() => {
        dragRef.current = null;
        setResizing(false);
      }}
      onLostPointerCapture={() => {
        dragRef.current = null;
        setResizing(false);
      }}
      onKeyDown={event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const panel = event.currentTarget.parentElement;
        const container = panel?.parentElement;
        if (!panel || !container) return;
        event.preventDefault();
        setPanelWidth(
          clampSidePanelWidth(
            panel.getBoundingClientRect().width + (event.key === 'ArrowLeft' ? 24 : -24),
            container.getBoundingClientRect().width - SIDE_PANEL_MAIN_MIN_WIDTH
          )
        );
      }}
      _hover={{ bg: 'accent.muted' }}
      _focusVisible={{ bg: 'accent.muted', outline: '2px solid', outlineColor: 'accent.solid' }}
      _after={{
        content: '""',
        position: 'absolute',
        top: 'calc(50% - 20px)',
        left: '3px',
        w: '2px',
        h: '40px',
        borderRadius: 'glassPill',
        bg: resizing ? 'accent.solid' : 'ui.border.strong',
      }}
    />
  );
}
